import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Play, Youtube, ExternalLink, Video } from "lucide-react";
import api from "@/lib/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Grid of Big Hits clips. Used on /big-hits and (with limit) on the homepage. */
export function BigHitsGrid({ hits, onOpen }) {
  if (!hits.length) {
    return <div className="text-zinc-500 text-sm">No hits added yet — check back after the next stream.</div>;
  }
  return (
    <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="big-hits-grid">
      {hits.map((h) => <BigHitCard key={h.id} hit={h} onOpen={() => onOpen(h)} />)}
    </div>
  );
}

function BigHitCard({ hit, onOpen }) {
  const canEmbed = hit.embed_url && hit.source_type !== "external" && hit.source_type !== "whatnot";
  const openable = canEmbed || hit.link_url;
  const Tag = openable ? "button" : "div";
  const [thumbErr, setThumbErr] = useState(false);
  const showThumb = hit.thumbnail_url && !thumbErr;
  const showUploadPoster = !showThumb && hit.source_type === "upload" && hit.embed_url;

  return (
    <Tag
      onClick={openable ? onOpen : undefined}
      data-testid={`big-hit-${hit.id}`}
      className="group relative aspect-video rounded-lg overflow-hidden border border-white/10 bg-[var(--cl-surface)] hover:border-[var(--cl-orange)] transition-colors text-left cursor-pointer w-full"
    >
      {showThumb && (
        <img src={hit.thumbnail_url} alt={hit.title} onError={() => setThumbErr(true)}
             className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
      )}
      {showUploadPoster && (
        <video muted preload="metadata" src={`${BACKEND_URL}${hit.embed_url}#t=0.1`}
               className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90" />
      )}
      {!showThumb && !showUploadPoster && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#2a0808] via-black to-[#1a0505] flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-black/50 backdrop-blur border border-white/10 flex items-center justify-center">
            <PlatformIcon type={hit.source_type} large />
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <span className="bg-black/60 backdrop-blur px-2 py-1 rounded text-[10px] uppercase tracking-widest text-[var(--cl-orange)] flex items-center gap-1">
          <PlatformIcon type={hit.source_type} />
          {hit.source_type}
        </span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-14 h-14 rounded-full bg-[var(--cl-primary)]/90 flex items-center justify-center">
          <Play size={22} className="text-white ml-1" fill="currentColor" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <div className="text-white font-medium text-sm line-clamp-2">{hit.title}</div>
        {(hit.player || hit.sport) && (
          <div className="text-xs text-zinc-300 mt-1">
            {[hit.player, hit.sport].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
    </Tag>
  );
}

function PlatformIcon({ type, large }) {
  const size = large ? 24 : 12;
  if (type === "youtube") return <Youtube size={size} />;
  if (type === "upload") return <Video size={size} />;
  return <ExternalLink size={size} />;
}

/** Modal player used by BigHitsGrid. */
export function BigHitModal({ hit, onClose }) {
  if (!hit) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}
         data-testid="big-hit-modal">
      <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-[var(--cl-orange)]"
              data-testid="big-hit-modal-close" aria-label="Close">
        <X size={28} />
      </button>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <PlayerFor hit={hit} />
        <div className="mt-4 flex flex-wrap items-baseline gap-3">
          <h3 className="font-display text-3xl text-white">{hit.title}</h3>
          {(hit.player || hit.sport) && (
            <span className="text-zinc-400 text-sm">
              {[hit.player, hit.sport].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
        {hit.description && <p className="text-zinc-300 mt-3 text-sm max-w-2xl">{hit.description}</p>}
      </div>
    </div>
  );
}

function PlayerFor({ hit }) {
  if (hit.source_type === "youtube" && hit.embed_url) {
    return (
      <div className="aspect-video w-full">
        <iframe title={hit.title} src={`${hit.embed_url}?autoplay=1`}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen className="w-full h-full rounded-lg" />
      </div>
    );
  }
  if (hit.source_type === "tiktok" && hit.embed_url) {
    return (
      <div className="w-full flex justify-center">
        <iframe title={hit.title} src={hit.embed_url}
                allow="autoplay; encrypted-media" allowFullScreen
                className="rounded-lg" style={{ width: 340, height: 720, maxHeight: "80vh" }} />
      </div>
    );
  }
  if (hit.source_type === "upload" && hit.embed_url) {
    return (
      <video src={`${BACKEND_URL}${hit.embed_url}`} controls autoPlay
             className="w-full max-h-[80vh] rounded-lg bg-black" data-testid="big-hit-video" />
    );
  }
  // Fallback: Whatnot / external — send them to the source.
  return (
    <div className="rounded-lg border border-white/10 bg-[var(--cl-surface)] p-12 text-center">
      <ExternalLink size={40} className="mx-auto text-[var(--cl-orange)]" />
      <h4 className="font-display text-2xl mt-4 text-white">Watch on {hit.source_type}</h4>
      <p className="text-zinc-400 text-sm mt-3 max-w-md mx-auto">
        This clip lives on {hit.source_type}. Click below to open it in a new tab.
      </p>
      <a href={hit.link_url} target="_blank" rel="noreferrer" className="btn-primary mt-6 inline-flex items-center gap-2">
        Open Clip <ExternalLink size={16} />
      </a>
    </div>
  );
}

/** Full page at /big-hits. */
export default function BigHits() {
  const [hits, setHits] = useState([]);
  const [settings, setSettings] = useState({});
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Big Hits | Card Lobster";
    Promise.all([api.get("/big-hits?limit=100"), api.get("/settings")])
      .then(([h, s]) => { setHits(h.data); setSettings(s.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
      <div className="max-w-3xl">
        <div className="text-label text-[var(--cl-primary)]">{settings.big_hits_kicker || "Big Hits"}</div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">{settings.big_hits_title || "Recent Bangers"}</h1>
        <p className="text-zinc-300 text-lg mt-4">
          {settings.big_hits_subtitle || "The heaviest pulls from our live streams and mystery pack rips."}
        </p>
      </div>
      <div className="mt-12">
        {loading
          ? <div className="text-zinc-500 text-sm">Loading…</div>
          : <BigHitsGrid hits={hits} onOpen={setActive} />}
      </div>
      <div className="mt-16 text-center border-t border-white/10 pt-10">
        <p className="text-zinc-400 text-sm">Want to be the first to see the next hit?</p>
        <Link to="/live" className="btn-primary inline-block mt-4">Watch Live on Whatnot</Link>
      </div>
      <BigHitModal hit={active} onClose={() => setActive(null)} />
    </div>
  );
}
